/**
 * Register — reproduction 1:1 de src/pages/Register.tsx du repo Loveable.
 * Différences :
 *   - Soumission via api.signup (vraie API JWT) au lieu du mock localStorage.
 *   - Mapping clientType : la maquette propose "individual / professional /
 *     company" qu'on traduit vers nos valeurs back-end (particulier /
 *     entreprise / promoteur).
 *   - Champs address/city/postalCode collectés mais pas encore envoyés à
 *     l'API signup (qui ne les attend pas dans son schéma actuel — ils
 *     seront utilisés à l'étape projet).
 *   - Stepper visuel identique : 3 étapes (Identité / Adresse / Sécurité)
 *     avec cercles numérotés + ligne de progression entre les étapes.
 */
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { packCodeSchema, type ClientType, type PackCode } from '@bcl/shared';
import {
  clearPackIntent,
  readPackIntent,
} from '@/pages/marketing/offres-page';
import { AddressAutocomplete } from '@/components/address-autocomplete';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/features/auth/error-message';
import { signup as apiSignup } from '@/features/auth/auth-api';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

const lovableToClientType: Record<string, ClientType> = {
  individual: 'particulier',
  professional: 'entreprise',
  company: 'entreprise',
};

export function SignupPage() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [clientType, setClientType] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [searchParams] = useSearchParams();

  function resolvePackIntent(): PackCode | null {
    const fromQuery = searchParams.get('pack');
    if (fromQuery) {
      const parsed = packCodeSchema.safeParse(fromQuery);
      if (parsed.success) return parsed.data;
    }
    return readPackIntent();
  }

  function handleNextStep() {
    if (currentStep === 1) {
      if (!firstName || !lastName || !email || !phone) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error('Veuillez saisir une adresse e-mail valide');
        return;
      }
      const phoneRegex = /^0[1-9]\d{8}$/;
      if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
        toast.error('Veuillez saisir un numéro de téléphone valide');
        return;
      }
    } else if (currentStep === 2) {
      if (!address || !city || !postalCode || !clientType) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      const postalCodeRegex = /^\d{5}$/;
      if (!postalCodeRegex.test(postalCode)) {
        toast.error('Veuillez saisir un code postal valide');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  }

  function handlePreviousStep() {
    setCurrentStep(currentStep - 1);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!password || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiSignup({
        firstName,
        lastName,
        email,
        phone: phone.replace(/\s/g, ''),
        password,
        clientType: lovableToClientType[clientType] ?? 'particulier',
      });
      setSession(res.token, res.user);
      toast.success('Inscription réussie');
      const intent = resolvePackIntent();
      if (intent) {
        clearPackIntent();
        navigate(`/projects/new?pack=${intent}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-bcl-grey">
      <div className="w-full max-w-2xl">
        <div className="text-sm mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link
              to="/"
              className="flex items-center text-gray-500 hover:text-bcl-blue"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à l&apos;accueil
            </Link>
          </Button>
        </div>
        <div className="bg-white rounded-xl shadow-lg px-8 py-10 animate-scale-in">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold mb-2">Créer un compte</h1>
            <p className="text-gray-600">
              Rejoignez BCL Telecom et accédez à nos services fibre optique
            </p>
          </div>

          {/* Progress steps */}
          <div className="flex items-center justify-between mb-8">
            <StepIndicator
              step={1}
              label="Identité"
              currentStep={currentStep}
              showConnector
            />
            <StepIndicator
              step={2}
              label="Adresse"
              currentStep={currentStep}
              showConnector
            />
            <StepIndicator
              step={3}
              label="Sécurité"
              currentStep={currentStep}
              showConnector={false}
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {currentStep === 1 && (
              <div className="space-y-4 animate-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Adresse e-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nom@exemple.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="address">Adresse *</Label>
                  <AddressAutocomplete
                    id="address"
                    placeholder="Commencez à saisir votre adresse…"
                    value={address}
                    onValueChange={setAddress}
                    onSelect={(sel) => {
                      setAddress(sel.address);
                      if (sel.postalCode) setPostalCode(sel.postalCode);
                      if (sel.city) setCity(sel.city);
                    }}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postalCode">Code postal *</Label>
                    <Input
                      id="postalCode"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Ville *</Label>
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientType">Type de client *</Label>
                  <Select value={clientType} onValueChange={setClientType}>
                    <SelectTrigger id="clientType">
                      <SelectValue placeholder="Sélectionnez votre profil" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Particulier</SelectItem>
                      <SelectItem value="professional">
                        Professionnel
                      </SelectItem>
                      <SelectItem value="company">Entreprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <p className="text-xs text-gray-500">
                    Minimum 8 caractères, au moins 1 lettre et 1 chiffre
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    Confirmer le mot de passe *
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="text-sm text-gray-600 p-4 bg-bcl-grey/50 rounded-lg">
                  <p>
                    En créant un compte, vous acceptez nos{' '}
                    <Link
                      to="/terms"
                      className="text-bcl-blue hover:underline"
                    >
                      Conditions d&apos;utilisation
                    </Link>{' '}
                    et notre{' '}
                    <Link
                      to="/privacy"
                      className="text-bcl-blue hover:underline"
                    >
                      Politique de confidentialité
                    </Link>
                    .
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              {currentStep > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePreviousStep}
                  disabled={isLoading}
                >
                  Précédent
                </Button>
              ) : (
                <span />
              )}
              {currentStep < 3 ? (
                <Button type="button" onClick={handleNextStep}>
                  Suivant
                </Button>
              ) : (
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Inscription en cours...
                    </>
                  ) : (
                    'Créer mon compte'
                  )}
                </Button>
              )}
            </div>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">Vous avez déjà un compte ?</span>{' '}
            <Link
              to="/login"
              className="text-bcl-blue hover:underline font-medium"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepIndicator({
  step,
  label,
  currentStep,
  showConnector,
}: {
  step: number;
  label: string;
  currentStep: number;
  showConnector: boolean;
}) {
  return (
    <div className="flex-1">
      <div className="relative flex items-center">
        <div
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
            currentStep >= step
              ? 'bg-bcl-blue text-white'
              : 'bg-gray-200 text-gray-600',
          )}
        >
          {currentStep > step ? <CheckCircle2 className="h-5 w-5" /> : step}
        </div>
        <div className="ml-2 text-sm font-medium">
          <div className={currentStep >= step ? 'text-black' : 'text-gray-500'}>
            {label}
          </div>
        </div>
        {showConnector && (
          <div
            className={cn(
              'flex-1 border-t ml-2',
              currentStep > step ? 'border-bcl-blue' : 'border-gray-200',
            )}
          />
        )}
      </div>
    </div>
  );
}
